import React, { useState } from 'react';
import { User, Users, Baby, ChevronRight, ChevronLeft, Eye, EyeOff, Plus, X, Check } from 'lucide-react';
import { Card } from './ui/card';
import DateInput from './DateInput';

const STEPS = ['Criancas', 'Responsaveis', 'Confirmar'];

const FormularioFamilia = ({ onSubmit, submitting, submitLabel = 'Cadastrar Familia' }) => {
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [resp1, setResp1] = useState({ nome: '', celular: '', email: '', parentesco: '' });
  const [resp2, setResp2] = useState({ nome: '', celular: '', parentesco: '', email: '' });
  const [showResp2, setShowResp2] = useState(false);
  const [password, setPassword] = useState('');
  const [criancas, setCriancas] = useState([{ nome: '', data_nascimento: '', sexo: '', celular: '' }]);

  const addCrianca = () => setCriancas(prev => [...prev, { nome: '', data_nascimento: '', sexo: '', celular: '' }]);
  const removeCrianca = (idx) => { if (criancas.length > 1) setCriancas(prev => prev.filter((_, i) => i !== idx)); };
  const updateCrianca = (idx, field, value) => setCriancas(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));

  // Step 0 (Criancas) - required: at least 1 child with nome + data_nascimento + sexo
  const validateStep0 = () => {
    const e = {};
    criancas.forEach((c, i) => {
      if (!c.nome.trim()) e[`c${i}_nome`] = true;
      if (!c.data_nascimento) e[`c${i}_nascimento`] = true;
      if (!c.sexo) e[`c${i}_sexo`] = true;
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Step 1 (Responsaveis) - fully optional; only validate coherence if any resp data was provided
  const validateStep1 = () => {
    const e = {};
    const resp1HasData = !!(resp1.nome.trim() || resp1.celular.trim() || resp1.email.trim() || password);
    if (resp1HasData) {
      if (!resp1.nome.trim()) e.resp1_nome = true;
      if (!resp1.celular.trim()) e.resp1_celular = true;
      if (!resp1.email.trim()) e.resp1_email = true;
      if (!password || password.length < 4) e.password = true;
    }
    if (showResp2 && (resp2.nome.trim() || resp2.celular.trim() || resp2.email.trim())) {
      if (!resp2.nome.trim()) e.resp2_nome = true;
      if (!resp2.celular.trim()) e.resp2_celular = true;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(prev => Math.min(prev + 1, 2));
  };

  const handleSubmit = () => {
    const resp1HasData = !!(resp1.nome.trim() && resp1.celular.trim() && resp1.email.trim());
    const data = {
      responsavel1: resp1HasData ? resp1 : null,
      responsavel2: showResp2 && resp2.nome.trim() && resp2.celular.trim() ? resp2 : null,
      password: resp1HasData ? password : '',
      criancas: criancas.map(c => ({
        nome: c.nome.trim(),
        data_nascimento: c.data_nascimento,
        sexo: c.sexo,
        celular: c.celular,
      })),
    };
    onSubmit(data);
  };

  const inputClass = (err) => `w-full border rounded-xl px-3 py-2.5 text-sm outline-none transition-all ${err ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-red-300' : 'border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] bg-white'}`;

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center justify-center gap-2 mb-2">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < step ? 'bg-[#1A4D2E] text-white' : i === step ? 'bg-[#F4D35E] text-[#1A4D2E]' : 'bg-gray-200 text-gray-500'
            }`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</div>
            <span className={`text-xs font-semibold hidden sm:inline ${i === step ? 'text-[#1A4D2E]' : 'text-gray-400'}`}>{s}</span>
            {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-[#1A4D2E]' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 0: Criancas (obrigatorio) */}
      {step === 0 && (
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <div className="flex items-center gap-2 mb-2">
            <Baby className="w-5 h-5 text-[#F4D35E]" />
            <h3 className="font-nunito font-bold text-base text-[#1A4D2E]">Criancas</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">Cadastre pelo menos uma crianca com as informacoes completas.</p>

          <div className="space-y-4">
            {criancas.map((c, idx) => (
              <div key={idx} className="bg-[#F9F9F7] rounded-xl p-4 border border-gray-100 relative">
                {criancas.length > 1 && (
                  <button onClick={() => removeCrianca(idx)} data-testid={`remove-child-${idx}`}
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                )}
                <p className="font-nunito font-bold text-xs text-[#1A4D2E] mb-3">Crianca {idx + 1}</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nome completo *</label>
                    <input data-testid={`child-nome-${idx}`} value={c.nome} onChange={e => updateCrianca(idx, 'nome', e.target.value)}
                      placeholder="Nome da crianca" className={inputClass(errors[`c${idx}_nome`])} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Data de nascimento *</label>
                      <DateInput data-testid={`child-nascimento-${idx}`} value={c.data_nascimento}
                        onChange={val => updateCrianca(idx, 'data_nascimento', val)}
                        className={inputClass(errors[`c${idx}_nascimento`])} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Sexo *</label>
                      <select data-testid={`child-sexo-${idx}`} value={c.sexo}
                        onChange={e => updateCrianca(idx, 'sexo', e.target.value)}
                        className={inputClass(errors[`c${idx}_sexo`])}>
                        <option value="">Selecione</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Celular da crianca (opcional)</label>
                    <input data-testid={`child-celular-${idx}`} type="tel" inputMode="numeric" value={c.celular}
                      onChange={e => updateCrianca(idx, 'celular', e.target.value)}
                      placeholder="Se a crianca tiver celular" className={inputClass(false)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button onClick={addCrianca} data-testid="add-child-btn"
            className="mt-3 w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-sm text-gray-500 hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-all flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Adicionar outra crianca
          </button>
        </Card>
      )}

      {/* Step 1: Responsaveis (opcional) */}
      {step === 1 && (
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#1A4D2E]" />
            <h3 className="font-nunito font-bold text-base text-[#1A4D2E]">Responsaveis (opcional)</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">Os responsaveis podem ser adicionados agora ou depois pelo Portal da Familia. Se informar um responsavel principal, tambem sera necessario definir uma senha para acesso.</p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nome completo</label>
              <input data-testid="resp1-nome" value={resp1.nome} onChange={e => setResp1(p => ({ ...p, nome: e.target.value }))}
                placeholder="Nome do responsavel" className={inputClass(errors.resp1_nome)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Celular</label>
                <input data-testid="resp1-celular" type="tel" inputMode="numeric" value={resp1.celular}
                  onChange={e => setResp1(p => ({ ...p, celular: e.target.value }))}
                  placeholder="11999001122" className={inputClass(errors.resp1_celular)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Parentesco</label>
                <select data-testid="resp1-parentesco" value={resp1.parentesco}
                  onChange={e => setResp1(p => ({ ...p, parentesco: e.target.value }))}
                  className={inputClass(false)}>
                  <option value="">Selecione</option>
                  <option value="Pai">Pai</option>
                  <option value="Mãe">Mae</option>
                  <option value="Avô">Avo</option>
                  <option value="Avó">Avo(a)</option>
                  <option value="Tio(a)">Tio(a)</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
              <input data-testid="resp1-email" type="email" value={resp1.email}
                onChange={e => setResp1(p => ({ ...p, email: e.target.value }))}
                placeholder="email@exemplo.com" className={inputClass(errors.resp1_email)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Senha da familia</label>
              <div className="relative">
                <input data-testid="family-password" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Minimo 4 caracteres" className={`${inputClass(errors.password)} pr-10`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Responsavel 2 */}
          {!showResp2 ? (
            <button onClick={() => setShowResp2(true)} data-testid="add-resp2"
              className="mt-4 w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-sm text-gray-500 hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-all flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Adicionar segundo responsavel
            </button>
          ) : (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#1A4D2E]" />
                  <h4 className="font-nunito font-bold text-sm text-[#1A4D2E]">Segundo Responsavel</h4>
                </div>
                <button onClick={() => { setShowResp2(false); setResp2({ nome: '', celular: '', parentesco: '', email: '' }); }}
                  className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Nome completo</label>
                  <input data-testid="resp2-nome" value={resp2.nome} onChange={e => setResp2(p => ({ ...p, nome: e.target.value }))}
                    placeholder="Nome" className={inputClass(errors.resp2_nome)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Celular</label>
                    <input data-testid="resp2-celular" type="tel" inputMode="numeric" value={resp2.celular}
                      onChange={e => setResp2(p => ({ ...p, celular: e.target.value }))}
                      placeholder="11999001122" className={inputClass(errors.resp2_celular)} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Parentesco</label>
                    <select data-testid="resp2-parentesco" value={resp2.parentesco}
                      onChange={e => setResp2(p => ({ ...p, parentesco: e.target.value }))}
                      className={inputClass(false)}>
                      <option value="">Selecione</option>
                      <option value="Pai">Pai</option>
                      <option value="Mãe">Mae</option>
                      <option value="Avô">Avo</option>
                      <option value="Avó">Avo(a)</option>
                      <option value="Tio(a)">Tio(a)</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-inter text-sm text-gray-700 mb-1 block">Email (opcional)</label>
                    <input data-testid="resp2-email" type="email" value={resp2.email}
                      onChange={e => setResp2(p => ({ ...p, email: e.target.value }))}
                      placeholder="email@exemplo.com" className={inputClass(false)} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Step 2: Confirmar */}
      {step === 2 && (
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <div className="flex items-center gap-2 mb-4">
            <Check className="w-5 h-5 text-[#1A4D2E]" />
            <h3 className="font-nunito font-bold text-base text-[#1A4D2E]">Confirmar Cadastro</h3>
          </div>

          <div className="space-y-3">
            {criancas.map((c, idx) => (
              <div key={idx} className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                <p className="text-[10px] text-amber-600 font-semibold uppercase mb-1">Crianca {idx + 1}</p>
                <p className="font-nunito font-bold text-sm text-[#1A4D2E]">{c.nome}</p>
                <p className="text-xs text-gray-500">
                  {c.data_nascimento ? new Date(c.data_nascimento + 'T12:00:00').toLocaleDateString('pt-BR') : ''} - {c.sexo === 'M' ? 'Masculino' : 'Feminino'}
                </p>
              </div>
            ))}

            {resp1.nome.trim() ? (
              <div className="bg-[#F2F7F4] rounded-xl p-3">
                <p className="text-[10px] text-gray-500 font-semibold uppercase mb-1">Responsavel Principal</p>
                <p className="font-nunito font-bold text-sm text-[#1A4D2E]">{resp1.nome}</p>
                <p className="text-xs text-gray-500">{resp1.celular} - {resp1.email}</p>
                {resp1.parentesco && <p className="text-xs text-gray-400">{resp1.parentesco}</p>}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-3 border border-dashed border-gray-200">
                <p className="text-xs text-gray-500 italic">Nenhum responsavel informado. A familia podera adicionar responsaveis depois.</p>
              </div>
            )}

            {showResp2 && resp2.nome.trim() && (
              <div className="bg-[#F2F7F4] rounded-xl p-3">
                <p className="text-[10px] text-gray-500 font-semibold uppercase mb-1">Segundo Responsavel</p>
                <p className="font-nunito font-bold text-sm text-[#1A4D2E]">{resp2.nome}</p>
                <p className="text-xs text-gray-500">{resp2.celular}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        {step > 0 && (
          <button onClick={() => setStep(prev => prev - 1)} data-testid="btn-prev-step"
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3.5 rounded-xl font-nunito font-bold text-sm flex items-center justify-center gap-2 transition-all">
            <ChevronLeft className="w-4 h-4" /> Voltar
          </button>
        )}
        {step < 2 ? (
          <button onClick={handleNext} data-testid="btn-next-step"
            className="flex-1 bg-[#1A4D2E] hover:bg-[#163f25] text-white py-3.5 rounded-xl font-nunito font-bold text-sm flex items-center justify-center gap-2 transition-all">
            Proximo <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting} data-testid="btn-submit-family"
            className="flex-1 bg-[#F4D35E] hover:bg-[#e8c84e] disabled:opacity-50 text-[#1A4D2E] py-3.5 rounded-xl font-nunito font-bold text-sm flex items-center justify-center gap-2 transition-all">
            <Check className="w-4 h-4" /> {submitting ? 'Cadastrando...' : submitLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default FormularioFamilia;
